import type { AdminUser, UserSession } from '@/pages/admin/users/columns'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import React, { useCallback, useEffect, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { UAParser } from 'ua-parser-js'
import type { UseMutationResult } from '@tanstack/react-query'
import type { authClient } from '@/lib/auth-client'
import { useTranslation } from 'react-i18next'

type AuthClientType = typeof authClient

interface RevokeSessionPayload {
	sessionToken: string
}

interface RevokeAllSessionsPayload {
	userId: string
}

interface ListSessionsSuccessData {
	sessions: UserSession[]
}

interface ManageSessionsDialogProps {
	user: AdminUser | null
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	authClient: AuthClientType
	revokeSessionMutation: UseMutationResult<unknown, Error, RevokeSessionPayload, unknown>
	revokeAllSessionsMutation: UseMutationResult<unknown, Error, RevokeAllSessionsPayload, unknown>
}

export function ManageSessionsDialog({
	user,
	isOpen,
	onOpenChange,
	authClient,
	revokeSessionMutation,
	revokeAllSessionsMutation,
}: ManageSessionsDialogProps) {
	const { t } = useTranslation()
	const [sessionsData, setSessionsData] = useState<UserSession[]>([])
	const [isLoadingSessions, setIsLoadingSessions] = useState(false)
	const [sessionsError, setSessionsError] = useState<string | null>(null)
	const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null)

	const fetchUserSessions = useCallback(
		async (userId: string) => {
			setIsLoadingSessions(true)
			setSessionsError(null)
			setSessionsData([])
			try {
				const result = await authClient.admin.listUserSessions({ userId })
				if (result.error) {
					throw new Error(
						result.error.message ||
							`${t('admin.users.page.sessionsDialog.errorPrefix')}: Failed to fetch sessions`
					)
				}
				if (!result.data || !Array.isArray(result.data.sessions)) {
					console.error('Unexpected response structure from listUserSessions API:', result)
					throw new Error(
						`${t('common.errorPrefix')}: Unexpected response structure from listUserSessions API`
					)
				}
				const sessions = result.data.sessions.map((session) => ({
					...session,
				})) as UserSession[]
				setSessionsData(sessions)
			} catch (error: unknown) {
				if (error instanceof Error) {
					setSessionsError(error.message)
				} else {
					setSessionsError(String(error))
				}
				setSessionsData([])
			} finally {
				setIsLoadingSessions(false)
			}
		},
		[authClient, t]
	)

	useEffect(() => {
		if (isOpen && user) {
			fetchUserSessions(user.id)
		} else if (!isOpen) {
			setSessionsData([])
			setIsLoadingSessions(false)
			setSessionsError(null)
			setRevokingTokenId(null)
		}
	}, [isOpen, user, fetchUserSessions])

	useEffect(() => {
		if (!revokeSessionMutation.isPending) {
			setRevokingTokenId(null)
		}
	}, [revokeSessionMutation.isPending])

	const handleRevokeSession = (sessionToken: string) => {
		setRevokingTokenId(sessionToken)
		revokeSessionMutation.mutate({ sessionToken })
	}

	const handleRevokeAllSessions = () => {
		if (user) {
			revokeAllSessionsMutation.mutate({ userId: user.id })
		}
	}

	const handleClose = () => {
		onOpenChange(false)
	}

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-[624px]'>
				<DialogHeader>
					<DialogTitle>
						{t('admin.users.page.sessionsDialog.title', {
							userIdentifier: user?.email || user?.id,
						})}
					</DialogTitle>
					<DialogDescription>{t('admin.users.page.sessionsDialog.description')}</DialogDescription>
				</DialogHeader>
				<div className='py-4'>
					{isLoadingSessions && <p>{t('admin.users.page.sessionsDialog.loading')}</p>}
					{sessionsError && (
						<p className='text-destructive'>
							{t('admin.users.page.sessionsDialog.errorPrefix')}: {sessionsError}
						</p>
					)}
					{!isLoadingSessions && !sessionsError && sessionsData.length === 0 && (
						<p>{t('admin.users.page.sessionsDialog.noSessions')}</p>
					)}
					{!isLoadingSessions && !sessionsError && sessionsData.length > 0 && (
						<ScrollArea className='h-72 w-full'>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('admin.users.page.sessionsDialog.headerBrowser')}</TableHead>
										<TableHead>{t('admin.users.page.sessionsDialog.headerOS')}</TableHead>
										<TableHead>{t('admin.users.page.sessionsDialog.headerIP')}</TableHead>
										<TableHead>{t('admin.users.page.sessionsDialog.headerExpires')}</TableHead>
										<TableHead className='text-right'>{t('common.actions')}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{sessionsData.map((session) => {
										const parser = new UAParser(session.userAgent || '')
										const result = parser.getResult()
										const browser = result.browser.name
											? `${result.browser.name} ${result.browser.version || ''}`.trim()
											: t('common.na')
										const os = result.os.name
											? `${result.os.name} ${result.os.version || ''}`.trim()
											: t('common.na')
										const expires = session.expiresAt
											? new Date(session.expiresAt).toLocaleString()
											: t('common.na')

										return (
											<TableRow key={session.id}>
												<TableCell className='text-xs'>{browser}</TableCell>
												<TableCell className='text-xs'>{os}</TableCell>
												<TableCell className='text-xs font-mono'>
													{session.ipAddress || t('common.na')}
												</TableCell>
												<TableCell className='text-xs'>{expires}</TableCell>
												<TableCell className='text-right'>
													<Button
														variant='destructive'
														size='sm'
														onClick={() => handleRevokeSession(session.token)}
														disabled={
															revokeSessionMutation.isPending &&
															revokingTokenId === session.token
														}
													>
														{revokeSessionMutation.isPending &&
														revokingTokenId === session.token
															? t('common.revoking')
															: t('admin.users.page.sessionsDialog.revokeButton')}
													</Button>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</ScrollArea>
					)}
				</div>
				<DialogFooter>
					<Button
						variant='destructive'
						onClick={handleRevokeAllSessions}
						disabled={revokeAllSessionsMutation.isPending || sessionsData.length === 0 || isLoadingSessions}
					>
						{revokeAllSessionsMutation.isPending
							? t('admin.users.page.sessionsDialog.revokingAll')
							: t('admin.users.page.sessionsDialog.revokeAllButton')}
					</Button>
					<Button variant='outline' onClick={handleClose}>
						{t('common.close')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
